import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { BuyingOrderLineType } from './buyingOrdersUrl';
import { isPotfLine } from './lineFilters';

/** Detail-pane group order: what a customer is waiting on comes first. */
export const LINE_GROUP_ORDER: readonly BuyingOrderLineType[] = [
  'back_order',
  'potf',
  'stock',
];

/**
 * The same rule as the backend's `line_type` (listBuyingOrders): a
 * sales-order-triggered line is a back order, or product on the fly when the
 * product is non-stock at the destination; everything else is stock fill.
 */
export function classifyLine(line: AssignmentLine): BuyingOrderLineType {
  if (line.trigger !== 'sales_order') return 'stock';
  return isPotfLine(line) ? 'potf' : 'back_order';
}

/**
 * A buyer-added stock line a later run merged its item's deficit into
 * (GEN-2938): the engine calculated a quantity for it.
 */
export function hasMergedDeficit(line: AssignmentLine): boolean {
  return line.trigger === 'manual' && line.neededQuantity > 0;
}

export interface GroupedLines {
  /** Lines of the allowed types, grouped in {@link LINE_GROUP_ORDER}. */
  lines: AssignmentLine[];
  counts: Record<BuyingOrderLineType, number>;
}

/**
 * Drops lines whose type the page filter excludes and stable-partitions the
 * rest into back orders, product on the fly, stock. Order inside a group is
 * the input order, so the server's ordering survives.
 */
export function groupLines(
  lines: readonly AssignmentLine[],
  allowed: readonly BuyingOrderLineType[],
): GroupedLines {
  const buckets: Record<BuyingOrderLineType, AssignmentLine[]> = {
    back_order: [],
    potf: [],
    stock: [],
  };
  for (const line of lines) {
    const type = classifyLine(line);
    if (allowed.includes(type)) buckets[type].push(line);
  }
  return {
    lines: LINE_GROUP_ORDER.flatMap((type) => buckets[type]),
    counts: {
      back_order: buckets.back_order.length,
      potf: buckets.potf.length,
      stock: buckets.stock.length,
    },
  };
}
